import React, { useCallback, useId, useState } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { brandLockup, statutoryEntries } from '../../config/brand';

/**
 * BrandAttribution renders the company behind the product.
 *
 * Two different things, two different renderings, never merged into one line:
 *
 *   variant="lockup"  One quiet line, "GreenFlow, from Attavor", plus a control
 *                     that expands the legal detail in place for anyone who
 *                     wants it. This is what goes in a page or sidebar footer.
 *   variant="legal"   The lockup plus the statutory block, always open. This is
 *                     what goes on an About or legal disclosure surface.
 *
 * WHY THE LEGAL BLOCK IS NEVER IN A HEADLINE
 *   ISO 9241-110 self-descriptiveness asks that a dialogue tell the user what it
 *   is without demanding attention it has not earned. A registered office is
 *   information a reader goes looking for, not information that should compete
 *   with the task in front of them. So it lives at the foot of the surface, and
 *   in the lockup variant it stays folded until asked for.
 *
 * WHY LINES DISAPPEAR RATHER THAN SHOW BLANKS
 *   Each statutory value is optional and omitted when unset. A stale or invented
 *   registration number is a false statement about a legal person; a missing
 *   line is merely a missing line. See frontend/src/config/brand.js.
 */

// Palettes are declared rather than computed so the contrast of every pairing
// could be measured rather than assumed. Measured with the WCAG 2.2 contrast
// formula against the surface each is actually used on. SC 1.4.3 wants 4.5:1:
//   light  text #4b5563 on #ffffff  = 7.56:1   (the login card)
//   light  text #4b5563 on #f0eeff  = 6.61:1   (the login page gradient)
//   light  link #4338ca on #ffffff  = 7.90:1
//   dark   text #9ca3af on #050608  = 7.98:1   (the registrant sidebar)
//   dark   text #9ca3af on #1a202c  = 6.43:1   (administrator sidebar, foot)
//   dark   text #9ca3af on #2d3748  = 4.72:1   (administrator sidebar, head)
//   dark   link #a5b4fc on #050608  = 10.17:1
// The muted grey many surfaces use for small print (#9ca3af on white) measures
// 2.54:1 and fails, so nothing a reader must read is rendered in it.
const PALETTE = {
  light: {
    lockup: '#4b5563',
    detail: '#4b5563',
    control: '#4338ca',
    rule: 'rgba(0,0,0,0.12)',
    focus: '#4338ca',
  },
  dark: {
    lockup: '#9ca3af',
    detail: '#9ca3af',
    control: '#a5b4fc',
    rule: 'rgba(255,255,255,0.14)',
    focus: '#a5b4fc',
  },
};

// WCAG 2.2 Success Criterion 2.5.8 (Target Size, Minimum) asks for 24 by 24 CSS
// pixels on any pointer target. The disclosure control and the privacy link are
// both small by design, so the minimum is set explicitly rather than left to the
// font size to decide.
const MIN_TARGET_PX = 24;

const srOnly = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

/**
 * @param {object} props
 * @param {'lockup'|'legal'} [props.variant] Which rendering to use.
 * @param {'auto'|'dark'|'light'} [props.tone] Colour tone. `auto` follows the
 *   application theme. Surfaces that are always dark regardless of theme, such
 *   as the administrator sidebar, pass `dark` so the text stays readable there.
 * @param {boolean} [props.collapsed] When true, render an accessible-name-only
 *   mark instead of the line, so a collapsed navigation rail is not widened.
 * @param {'left'|'center'} [props.align] Text alignment.
 * @param {boolean} [props.showLegalLink] Whether the lockup carries its own
 *   "Legal information" disclosure. Defaults to true, which is every existing
 *   caller. The registrant navigation rail passes false because the same
 *   disclosure is rendered in full on Settings, Build version, and one action
 *   with two homes is a defect under ISO 9241-110 conformity rather than a
 *   convenience. The lockup itself is never suppressed: attribution to the
 *   legal entity belongs on every screen.
 * @param {string} [props.className] Optional class for the wrapper.
 */
const BrandAttribution = ({
  variant = 'lockup',
  tone = 'auto',
  collapsed = false,
  align = 'left',
  showLegalLink = true,
  className = '',
}) => {
  const { isDark } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const detailsId = useId();

  const resolvedTone = tone === 'auto' ? (isDark ? 'dark' : 'light') : tone;
  const colors = PALETTE[resolvedTone] || PALETTE.light;

  const lockup = brandLockup();
  const entries = statutoryEntries();

  const handleToggle = useCallback((event) => {
    // The control sits inside sidebars whose ancestors listen for clicks to
    // close themselves. Opening the legal text must not also dismiss the
    // surface the reader is reading it on.
    event.preventDefault();
    event.stopPropagation();
    setIsOpen((open) => !open);
  }, []);

  // A collapsed rail has room for a mark and nothing else. The full lockup still
  // reaches assistive technology through the accessible name, so the information
  // is not lost, only the space it would have taken.
  if (collapsed) {
    const mark = (lockup || '?').trim().charAt(0).toUpperCase();
    return (
      <div
        className={className}
        style={{ display: 'flex', justifyContent: 'center', padding: '8px 0' }}
      >
        <span
          role="img"
          aria-label={lockup}
          title={lockup}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: MIN_TARGET_PX,
            height: MIN_TARGET_PX,
            borderRadius: '50%',
            border: `1px solid ${colors.rule}`,
            color: colors.lockup,
            fontSize: '11px',
            fontWeight: 600,
            lineHeight: 1,
          }}
        >
          {mark}
        </span>
      </div>
    );
  }

  // With the disclosure link suppressed there is no control that could set
  // isOpen, but tying the panel to the link as well means a future caller
  // cannot end up with an open panel and no way to close it.
  const showDetails = variant === 'legal' || (showLegalLink && isOpen);

  const detailList = entries.length > 0 ? (
    <dl
      id={detailsId}
      style={{
        margin: '8px 0 0',
        paddingTop: '8px',
        borderTop: `1px solid ${colors.rule}`,
        color: colors.detail,
        fontSize: '11px',
        lineHeight: 1.5,
      }}
    >
      {/* One term, several descriptions. The term is announced but not shown,
          because on screen the heading above it already says what this is. */}
      <dt style={srOnly}>Company disclosure</dt>
      {entries.map(({ text, href }) => (
        <dd key={text} style={{ margin: '0 0 3px' }}>
          {href ? (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: colors.control,
                textDecoration: 'underline',
                display: 'inline-block',
                minHeight: `${MIN_TARGET_PX}px`,
                lineHeight: `${MIN_TARGET_PX}px`,
              }}
            >
              {text}
            </a>
          ) : (
            text
          )}
        </dd>
      ))}
    </dl>
  ) : null;

  return (
    <div
      className={className}
      style={{
        textAlign: align,
        // 320 pixels wide with no horizontal scroll: a registered office is a
        // long string and must be allowed to wrap rather than push the layout.
        overflowWrap: 'anywhere',
        wordBreak: 'break-word',
      }}
    >
      <p
        style={{
          margin: 0,
          color: colors.lockup,
          fontSize: '12px',
          lineHeight: 1.5,
          fontWeight: 500,
        }}
      >
        {lockup}
      </p>

      {variant === 'lockup' && showLegalLink && entries.length > 0 && (
        <button
          type="button"
          onClick={handleToggle}
          aria-expanded={isOpen}
          aria-controls={detailsId}
          style={{
            marginTop: '2px',
            padding: 0,
            minHeight: `${MIN_TARGET_PX}px`,
            background: 'transparent',
            border: 'none',
            color: colors.control,
            font: 'inherit',
            fontSize: '11px',
            textDecoration: 'underline',
            cursor: 'pointer',
            outlineColor: colors.focus,
          }}
        >
          {isOpen ? 'Hide legal information' : 'Legal information'}
        </button>
      )}

      {showDetails && detailList}
    </div>
  );
};

export default BrandAttribution;
